import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchProfileById } from "@/api/profile";
import { Button } from "@/components/ui/Button";
import {
  buildAndroidIntentInviteUrl,
  buildInviteWebUrl,
  buildPlayStoreInviteUrl,
  isAndroidDevice,
  isInviteUserId,
} from "@/lib/invite";
import { tw } from "@/lib/theme";
import type { UserProfile } from "@/lib/types";

function setMeta(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function InviteLandingPage() {
  const { userId } = useParams<{ userId: string }>();
  const validId = isInviteUserId(userId) ? userId : null;
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(Boolean(validId));

  const inviterName = profile?.fullName?.trim() || "A friend";
  const inviteUrl = validId ? buildInviteWebUrl(validId) : `https://www.daywia.com/invite`;
  const playStoreUrl = validId
    ? buildPlayStoreInviteUrl(validId)
    : `https://play.google.com/store/apps/details?id=com.dhrudhew.daywia`;

  const pageTitle = useMemo(
    () => `${inviterName} invited you to Daywia`,
    [inviterName],
  );

  useEffect(() => {
    if (!validId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchProfileById(validId)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [validId]);

  useEffect(() => {
    document.title = pageTitle;
    const description =
      "Join Daywia — photo-first social. Download the Android app and sign up.";
    setMetaName("description", description);
    setMeta("og:title", pageTitle);
    setMeta("og:description", description);
    setMeta("og:url", inviteUrl);
    setMeta("og:type", "website");
    setMeta("og:site_name", "Daywia");
    if (profile?.avatarUrl) {
      setMeta("og:image", profile.avatarUrl);
    }
  }, [pageTitle, inviteUrl, profile?.avatarUrl]);

  function onGetApp() {
    if (validId && isAndroidDevice()) {
      window.location.href = buildAndroidIntentInviteUrl(validId);
      window.setTimeout(() => {
        window.location.href = playStoreUrl;
      }, 1400);
      return;
    }
    window.location.href = playStoreUrl;
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-10 dark:bg-dark-canvas sm:px-6">
      <div className={`w-full max-w-md space-y-8 ${tw.card} p-6 sm:p-8`}>
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary dark:text-primary-dark">
            Daywia
          </p>
          <h1 className={`mt-3 text-2xl tracking-tight sm:text-3xl ${tw.heading}`}>
            {loading ? "Loading invite…" : pageTitle}
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${tw.muted}`}>
            Photo-first social on Android. Sign up to connect with friends and earn
            Sparks together.
          </p>
        </div>

        {!loading && profile ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={profile.avatarUrl}
              alt=""
              className="h-20 w-20 rounded-full border-2 border-primary/20 object-cover dark:border-primary-dark/30"
            />
            <p className={`text-sm ${tw.muted}`}>
              @{profile.username}
            </p>
          </div>
        ) : null}

        <div className="space-y-3">
          <Button fullWidth onClick={onGetApp}>
            Get Daywia on Android
          </Button>
          <p className={`text-center text-xs ${tw.muted}`}>
            Free on Google Play. Your invite is applied when you install and sign
            up.
          </p>
        </div>
      </div>

      <p className={`mt-8 max-w-sm text-center text-xs ${tw.muted}`}>
        Already have the app? Open this link on your phone — Daywia will pick up
        your invite automatically.
      </p>
    </div>
  );
}
